import React from 'react'
import { AppLayout } from '../../components/layout/AppLayout'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { ComplianceDisclaimer } from '../../components/ComplianceDisclaimer'
import { apiFetch, openPdfInNewTab } from '../../lib/api-client'
import { X, FileText, ChevronDown, ChevronUp } from 'lucide-react'

// ─── Types ────────────────────────────────────────────────────────────────────

interface OverduePayment {
  id: string
  lease_id: string
  lease_unit_no?: string | null
  lease_address?: string | null
  renter_name?: string | null
  period_year: number
  period_month: number
  expected_oere: number
  received_oere: number
  status: 'overdue' | 'partial'
  due_date?: string | null
}

interface PaymentsResponse {
  items: OverduePayment[]
  total?: number
}

interface Invoice {
  id: string
  lease_id: string
  status: string
  amount_oere?: number
  due_date?: string | null
}

// ─── Invoice lookup ───────────────────────────────────────────────────────────

/**
 * The unpaid invoice a restance row refers to. Previously each caller took the
 * newest invoice with status 'sent', which skipped invoices already marked
 * 'overdue' and, on a lease with several open invoices, picked next month's
 * charge instead of the one in arrears. Prefer the open invoice due in the
 * payment's period; otherwise the oldest open one.
 */
async function fetchOpenInvoiceForPayment(payment: OverduePayment): Promise<Invoice | null> {
  const res = await apiFetch<Invoice[] | { items: Invoice[] }>(`/api/v1/invoices?lease_id=${payment.lease_id}&limit=250`)
  const items = (Array.isArray(res) ? res : (res.items ?? []))
    .filter(i => i.status === 'sent' || i.status === 'overdue')
  if (items.length === 0) return null
  const period = `${payment.period_year}-${String(payment.period_month).padStart(2, '0')}`
  const inPeriod = items.find(i => (i.due_date ?? '').startsWith(period))
  if (inPeriod) return inPeriod
  return [...items].sort((a, b) => (a.due_date ?? '9999').localeCompare(b.due_date ?? '9999'))[0]
}

interface Rykker {
  id: string
  invoice_id: string
  rykker_number: number
  gebyr_oere: number
  status: string
  sent_at?: string | null
  created_at?: string
  eligibility?: {
    eligible: boolean
    reason?: string
    days_since_last?: number
  }
}

interface PaakravFrist {
  earliest_send_date: string
  deadline: string
  ophavelse_eligible_from: string
}

interface Paakrav {
  id: string
  invoice_id: string
  gebyr_oere: number
  total_debt_oere: number
  cure_deadline: string | null
  status: string
  sent_at?: string | null
  physical_letter_required: boolean
  rykker_gebyr_oere?: number
  email_sent?: boolean
  frist?: PaakravFrist
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const MONTHS_DA = ['Januar', 'Februar', 'Marts', 'April', 'Maj', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'December']

function formatDKK(oere: number): string {
  return (oere / 100).toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' kr'
}

function daysSince1st(year: number, month: number): number {
  const first = new Date(year, month - 1, 1)
  const now = new Date()
  return Math.max(0, Math.floor((now.getTime() - first.getTime()) / (1000 * 60 * 60 * 24)))
}

function addDays(dateStr: string, days: number): string {
  const d = new Date(dateStr)
  d.setDate(d.getDate() + days)
  return d.toLocaleDateString('da-DK', { day: 'numeric', month: 'long' })
}

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280',
  textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 5,
}

const inputStyle: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', height: 34, padding: '0 10px',
  border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, color: '#111827',
  background: '#fff', fontFamily: 'inherit', outline: 'none',
}

const secondaryBtnStyle: React.CSSProperties = {
  background: 'white', border: '1px solid #d1d5db', color: '#374151',
  borderRadius: 7, padding: '6px 12px', fontSize: 13, cursor: 'pointer',
  fontFamily: 'inherit', fontWeight: 500,
}

// ─── Toast ────────────────────────────────────────────────────────────────────

interface ToastMessage {
  id: number
  text: string
  kind: 'success' | 'error'
}

function ToastContainer({ toasts, onRemove }: { toasts: ToastMessage[]; onRemove: (id: number) => void }) {
  return (
    <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 999, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {toasts.map(t => (
        <div key={t.id} style={{
          padding: '10px 16px', borderRadius: 8, fontSize: 13, fontWeight: 500,
          background: t.kind === 'success' ? '#16a34a' : '#dc2626', color: '#fff',
          boxShadow: '0 4px 16px rgba(0,0,0,0.18)', display: 'flex', alignItems: 'center', gap: 10, minWidth: 240,
        }}>
          <span style={{ flex: 1 }}>{t.text}</span>
          <button onClick={() => onRemove(t.id)} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: 0, opacity: 0.7 }}>
            <X size={14} />
          </button>
        </div>
      ))}
    </div>
  )
}

function useToast() {
  const [toasts, setToasts] = React.useState<ToastMessage[]>([])
  const nextId = React.useRef(0)
  const show = React.useCallback((text: string, kind: 'success' | 'error') => {
    const id = nextId.current++
    setToasts(prev => [...prev, { id, text, kind }])
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000)
  }, [])
  const remove = React.useCallback((id: number) => setToasts(prev => prev.filter(t => t.id !== id)), [])
  return { toasts, show, remove }
}

// ─── Mark Paid Modal ──────────────────────────────────────────────────────────

interface MarkPaidModalProps {
  payment: OverduePayment
  onClose: () => void
  onSaved: () => void
}

function MarkPaidModal({ payment, onClose, onSaved }: MarkPaidModalProps) {
  const defaultAmount = ((payment.expected_oere - payment.received_oere) / 100).toFixed(2)
  const [receivedDKK, setReceivedDKK] = React.useState(defaultAmount)
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  async function save() {
    setSaving(true)
    setError(null)
    try {
      const received_oere = Math.round((parseFloat(receivedDKK.replace(',', '.')) || 0) * 100)
      const total_received = payment.received_oere + received_oere
      const newStatus = total_received >= payment.expected_oere ? 'paid' : 'partial'
      await apiFetch(`/api/v1/rent-payments/${payment.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          status: newStatus,
          received_oere: total_received,
          paid_at: new Date().toISOString().slice(0, 10),
        }),
      })
      onSaved()
      onClose()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Kunne ikke registrere betaling.')
    } finally {
      setSaving(false)
    }
  }

  const mangler = payment.expected_oere - payment.received_oere

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div style={{
        background: '#fff', borderRadius: 10, padding: 24, width: 360,
        boxShadow: '0 8px 40px rgba(0,0,0,0.15)', border: '1px solid #e8eaed',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>Markér betalt</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 4 }}>
            <X size={16} />
          </button>
        </div>

        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 16 }}>
          {payment.lease_unit_no ?? payment.lease_id} · {MONTHS_DA[(payment.period_month - 1)]} {payment.period_year}
          {payment.renter_name && <div style={{ marginTop: 2 }}>{payment.renter_name}</div>}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16, fontSize: 12 }}>
          <div style={{ padding: '8px 12px', background: '#f9fafb', borderRadius: 6 }}>
            <div style={{ color: '#6b7280', marginBottom: 2 }}>Forventet</div>
            <div style={{ fontWeight: 600, color: '#111827' }}>{formatDKK(payment.expected_oere)}</div>
          </div>
          <div style={{ padding: '8px 12px', background: '#fef2f2', borderRadius: 6 }}>
            <div style={{ color: '#6b7280', marginBottom: 2 }}>Mangler</div>
            <div style={{ fontWeight: 600, color: '#dc2626' }}>{formatDKK(mangler)}</div>
          </div>
        </div>

        {error && <div style={{ background: '#fef2f2', color: '#dc2626', fontSize: 12, padding: '6px 10px', borderRadius: 6, marginBottom: 12, border: '1px solid #fecaca' }}>{error}</div>}

        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>Modtaget beløb (kr)</label>
          <input
            style={inputStyle}
            value={receivedDKK}
            onChange={e => setReceivedDKK(e.target.value)}
            placeholder="0.00"
          />
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{
            padding: '7px 14px', borderRadius: 6, fontSize: 13, fontWeight: 500,
            background: '#f9fafb', color: '#374151', border: '1px solid #e5e7eb', cursor: 'pointer',
          }}>Annuller</button>
          <Button size="sm" isLoading={saving} onClick={save}>Markér betalt</Button>
        </div>
      </div>
    </div>
  )
}

// ─── Rykker History Panel ─────────────────────────────────────────────────────

function RykkerHistoryPanel({ rykkere, onClose }: { rykkere: Rykker[]; onClose: () => void }) {
  return (
    <div style={{
      position: 'absolute', right: 0, top: '100%', zIndex: 100, marginTop: 4,
      background: '#fff', borderRadius: 8, border: '1px solid #e8eaed',
      boxShadow: '0 6px 24px rgba(0,0,0,0.12)', minWidth: 280, padding: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#111827' }}>Rykkerhistorik</div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 2 }}>
          <X size={13} />
        </button>
      </div>
      {rykkere.length === 0 ? (
        <div style={{ fontSize: 12, color: '#9ca3af', padding: '6px 0' }}>Ingen rykkere sendt</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {rykkere.map(r => (
            <div key={r.id} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '6px 8px', background: '#f9fafb', borderRadius: 6, fontSize: 12,
            }}>
              <div>
                <span style={{ fontWeight: 600, color: '#111827' }}>Rykker {r.rykker_number}</span>
                <span style={{ color: '#9ca3af', marginLeft: 6 }}>
                  {r.sent_at ? new Date(r.sent_at).toLocaleDateString('da-DK') : '—'}
                </span>
              </div>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); openPdfInNewTab(`/api/v1/rykkere/${r.id}/pdf`).catch((err: unknown) => alert(err instanceof Error ? err.message : 'Kunne ikke hente PDF')) }}
                style={{ color: '#4f46e5', display: 'flex', alignItems: 'center', gap: 3 }}
                title="Åbn PDF"
              >
                <FileText size={12} />
                <span style={{ fontSize: 11 }}>PDF</span>
              </a>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Rykker Badge ─────────────────────────────────────────────────────────────

function RykkerBadge({ invoiceId }: { invoiceId: string | null }) {
  const [open, setOpen] = React.useState(false)
  const [rykkere, setRykkere] = React.useState<Rykker[] | null>(null)
  const [loading, setLoading] = React.useState(false)
  const ref = React.useRef<HTMLDivElement>(null)

  async function loadRykkere() {
    if (!invoiceId || rykkere !== null) return
    setLoading(true)
    try {
      const result = await apiFetch<Rykker[]>(`/api/v1/rykkere?invoice_id=${invoiceId}`)
      setRykkere(Array.isArray(result) ? result : [])
    } catch {
      setRykkere([])
    } finally {
      setLoading(false)
    }
  }

  function toggle() {
    if (!open) loadRykkere()
    setOpen(v => !v)
  }

  // Close on outside click
  React.useEffect(() => {
    if (!open) return
    function handle(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handle)
    return () => document.removeEventListener('mousedown', handle)
  }, [open])

  if (!invoiceId) {
    return (
      <span style={{
        padding: '2px 7px', borderRadius: 10, fontSize: 11, fontWeight: 500,
        background: '#f3f4f6', color: '#9ca3af', border: '1px solid #e5e7eb',
      }}>
        0 rykkere
      </span>
    )
  }

  const count = rykkere !== null ? rykkere.length : '…'

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={toggle}
        style={{
          padding: '2px 7px', borderRadius: 10, fontSize: 11, fontWeight: 500,
          background: (rykkere?.length ?? 0) > 0 ? '#eff6ff' : '#f3f4f6',
          color: (rykkere?.length ?? 0) > 0 ? '#4f46e5' : '#6b7280',
          border: `1px solid ${(rykkere?.length ?? 0) > 0 ? '#bfdbfe' : '#e5e7eb'}`,
          cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 3,
        }}
      >
        {loading ? '…' : `${count} rykker${count === 1 ? '' : 'e'}`}
        {open ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
      </button>
      {open && rykkere !== null && (
        <RykkerHistoryPanel rykkere={rykkere} onClose={() => setOpen(false)} />
      )}
    </div>
  )
}

// ─── Rykker Modal ─────────────────────────────────────────────────────────────

interface RykkerModalProps {
  payment: OverduePayment
  onClose: () => void
  onSent: () => void
  showToast: (text: string, kind: 'success' | 'error') => void
}

const GEBYR_OERE = 10000 // 100 kr
const PAAKRAV_GEBYR_OERE = 33500 // 335 kr — 2026 rate (VEJ nr 9886 af 04/09/2025)

function RykkerModal({ payment, onClose, onSent, showToast }: RykkerModalProps) {
  const [invoice, setInvoice] = React.useState<Invoice | null>(null)
  const [rykkere, setRykkere] = React.useState<Rykker[] | null>(null)
  const [loadingData, setLoadingData] = React.useState(true)
  const [sending, setSending] = React.useState(false)
  const [loadError, setLoadError] = React.useState<string | null>(null)

  React.useEffect(() => {
    (async () => {
      setLoadingData(true)
      setLoadError(null)
      try {
        const inv = await fetchOpenInvoiceForPayment(payment)
        setInvoice(inv)
        if (inv) {
          const r = await apiFetch<Rykker[]>(`/api/v1/rykkere?invoice_id=${inv.id}`)
          setRykkere(Array.isArray(r) ? r : [])
        } else {
          setRykkere([])
        }
      } catch {
        setLoadError('Kunne ikke hente fakturadata.')
        setRykkere([])
      } finally {
        setLoadingData(false)
      }
    })()
  }, [payment.lease_id])

  async function sendRykker() {
    if (!invoice) return
    setSending(true)
    try {
      const generated = await apiFetch<Rykker>('/api/v1/rykkere/generate', {
        method: 'POST',
        body: JSON.stringify({ invoice_id: invoice.id }),
      })
      await apiFetch(`/api/v1/rykkere/${generated.id}/send`, { method: 'POST' })
      showToast(`Rykker nr. ${generated.rykker_number} sendt til ${payment.renter_name ?? 'lejer'}`, 'success')
      onSent()
      onClose()
    } catch (e: unknown) {
      showToast(e instanceof Error ? e.message : 'Kunne ikke sende rykker.', 'error')
    } finally {
      setSending(false)
    }
  }

  const sentRykkere = (rykkere ?? []).filter(r => r.status === 'sent')
  const nextNumber = sentRykkere.length + 1
  const maxReached = sentRykkere.length >= 3

  // Check 10-day cooldown from most recently sent
  const lastSent = sentRykkere.length > 0
    ? sentRykkere.reduce((a, b) => (!a.sent_at || (b.sent_at && b.sent_at > a.sent_at) ? b : a), sentRykkere[0])
    : null
  const cooldownActive = lastSent?.sent_at
    ? (new Date().getTime() - new Date(lastSent.sent_at).getTime()) < 10 * 24 * 60 * 60 * 1000
    : false
  const nextAllowedDate = lastSent?.sent_at ? addDays(lastSent.sent_at, 10) : null

  const mangler = payment.expected_oere - payment.received_oere
  const totalWithGebyr = mangler + GEBYR_OERE

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div style={{
        background: '#fff', borderRadius: 10, padding: 24, width: 420,
        boxShadow: '0 8px 40px rgba(0,0,0,0.15)', border: '1px solid #e8eaed',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>
            Send rykker til {payment.renter_name ?? 'lejer'}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 4 }}>
            <X size={16} />
          </button>
        </div>

        {loadingData ? (
          <div style={{ padding: '24px 0', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Henter…</div>
        ) : loadError ? (
          <div style={{ padding: 12, background: '#fef2f2', color: '#dc2626', fontSize: 13, borderRadius: 6, marginBottom: 12 }}>
            {loadError}
          </div>
        ) : !invoice ? (
          <div style={{ padding: 12, background: '#fffbeb', color: '#d97706', fontSize: 13, borderRadius: 6, marginBottom: 12 }}>
            Ingen faktura fundet for dette lejeforhold. Opret og send en faktura først.
          </div>
        ) : (
          <>
            {/* Invoice ref */}
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 14 }}>
              <span style={{ fontWeight: 500 }}>{payment.lease_unit_no ?? payment.lease_id}</span>
              {' · '}{MONTHS_DA[payment.period_month - 1]} {payment.period_year}
              <span style={{ marginLeft: 8, color: '#9ca3af' }}>Faktura #{invoice.id.slice(0, 8)}</span>
            </div>

            {/* Amount breakdown */}
            <div style={{ background: '#f9fafb', borderRadius: 8, padding: 12, marginBottom: 14, fontSize: 13 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, color: '#6b7280' }}>
                <span>Restance</span>
                <span style={{ fontFamily: 'monospace' }}>{formatDKK(mangler)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, color: '#6b7280' }}>
                <span>Rykkergebyr</span>
                <span style={{ fontFamily: 'monospace' }}>{formatDKK(GEBYR_OERE)}</span>
              </div>
              <div style={{ borderTop: '1px solid #e8eaed', paddingTop: 8, display: 'flex', justifyContent: 'space-between', fontWeight: 700, color: '#111827' }}>
                <span>I alt</span>
                <span style={{ fontFamily: 'monospace' }}>{formatDKK(totalWithGebyr)}</span>
              </div>
            </div>

            {/* Rykker history summary */}
            {sentRykkere.length > 0 && (
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>
                Tidligere rykkere: {sentRykkere.map(r => `#${r.rykker_number}`).join(', ')}
              </div>
            )}

            {/* Status / action */}
            {maxReached ? (
              <div style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, fontSize: 13, color: '#dc2626', marginBottom: 14 }}>
                Maks. 3 rykkere nået — overvej påkrav
              </div>
            ) : cooldownActive ? (
              <div style={{ padding: '10px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, fontSize: 13, color: '#d97706', marginBottom: 14 }}>
                Næste rykker kan sendes {nextAllowedDate}
              </div>
            ) : null}
          </>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
          <button onClick={onClose} style={{
            padding: '7px 14px', borderRadius: 6, fontSize: 13, fontWeight: 500,
            background: '#f9fafb', color: '#374151', border: '1px solid #e5e7eb', cursor: 'pointer',
          }}>Annuller</button>
          <Button
            size="sm"
            isLoading={sending}
            disabled={!invoice || maxReached || cooldownActive || loadingData || !!loadError}
            onClick={sendRykker}
          >
            Generer og send rykker nr. {nextNumber}
          </Button>
        </div>
      </div>
    </div>
  )
}

// ─── Påkrav Modal ─────────────────────────────────────────────────────────────

interface PaakravModalProps {
  payment: OverduePayment
  onClose: () => void
  onSent: () => void
  showToast: (text: string, kind: 'success' | 'error') => void
}

function PaakravModal({ payment, onClose, onSent, showToast }: PaakravModalProps) {
  const [invoice, setInvoice] = React.useState<Invoice | null>(null)
  const [paakrav, setPaakrav] = React.useState<Paakrav | null>(null)
  const [loadingData, setLoadingData] = React.useState(true)
  const [loadError, setLoadError] = React.useState<string | null>(null)
  const [eligibilityError, setEligibilityError] = React.useState<string | null>(null)
  const [sending, setSending] = React.useState(false)

  React.useEffect(() => {
    (async () => {
      setLoadingData(true)
      setLoadError(null)
      setEligibilityError(null)
      try {
        const inv = await fetchOpenInvoiceForPayment(payment)
        setInvoice(inv)
        if (inv) {
          // Dry-run eligibility check by calling generate
          try {
            const p = await apiFetch<Paakrav>('/api/v1/paakrav/generate', {
              method: 'POST',
              body: JSON.stringify({ invoice_id: inv.id }),
            })
            setPaakrav(p)
          } catch (e) {
            setEligibilityError(e instanceof Error ? e.message : 'Ikke berettiget til påkrav.')
          }
        }
      } catch {
        setLoadError('Kunne ikke hente fakturadata.')
      } finally {
        setLoadingData(false)
      }
    })()
  }, [payment.lease_id])

  async function sendPaakrav() {
    if (!paakrav) return
    setSending(true)
    try {
      await apiFetch(`/api/v1/paakrav/${paakrav.id}/send`, { method: 'POST' })
      showToast(`Påkrav sendt til ${payment.renter_name ?? 'lejer'} — betalingsfrist ${paakrav.cure_deadline ?? '—'}`, 'success')
      onSent()
      onClose()
    } catch (e: unknown) {
      showToast(e instanceof Error ? e.message : 'Kunne ikke sende påkrav.', 'error')
    } finally {
      setSending(false)
    }
  }

  const canSend = !!paakrav && !eligibilityError && !loadError && !loadingData

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 200,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div style={{
        background: '#fff', borderRadius: 10, padding: 24, width: 460,
        boxShadow: '0 8px 40px rgba(0,0,0,0.18)', border: '2px solid #fecaca',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#991b1b' }}>Send påkrav — § 182</div>
            <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>
              Formel betalingsopfordring til {payment.renter_name ?? 'lejer'}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 4 }}>
            <X size={16} />
          </button>
        </div>

        {loadingData ? (
          <div style={{ padding: '24px 0', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Beregner…</div>
        ) : loadError ? (
          <div style={{ padding: 12, background: '#fef2f2', color: '#dc2626', fontSize: 13, borderRadius: 6, marginBottom: 12 }}>
            {loadError}
          </div>
        ) : !invoice ? (
          <div style={{ padding: 12, background: '#fffbeb', color: '#d97706', fontSize: 13, borderRadius: 6, marginBottom: 12 }}>
            Ingen faktura fundet for dette lejeforhold. Opret en faktura først.
          </div>
        ) : eligibilityError ? (
          <div style={{ padding: 12, background: '#fef2f2', color: '#dc2626', fontSize: 13, borderRadius: 6, marginBottom: 12, border: '1px solid #fecaca' }}>
            <strong>Ikke berettiget:</strong> {eligibilityError}
          </div>
        ) : paakrav ? (
          <>
            {/* Debt breakdown */}
            <div style={{ background: '#f9fafb', borderRadius: 8, padding: 14, marginBottom: 14, fontSize: 13 }}>
              <div style={{ fontWeight: 600, color: '#374151', marginBottom: 10, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Gældsspecifikation
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, color: '#6b7280' }}>
                <span>Husleje (faktura)</span>
                <span style={{ fontFamily: 'monospace' }}>{formatDKK(invoice.amount_oere ?? 0)}</span>
              </div>
              {(paakrav.rykker_gebyr_oere ?? 0) > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, color: '#6b7280' }}>
                  <span>Rykkergebyrer (tidligere)</span>
                  <span style={{ fontFamily: 'monospace' }}>{formatDKK(paakrav.rykker_gebyr_oere ?? 0)}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, color: '#b45309' }}>
                <span>Påkravsgebyr (335,00 kr — ikke moms)</span>
                <span style={{ fontFamily: 'monospace' }}>{formatDKK(PAAKRAV_GEBYR_OERE)}</span>
              </div>
              <div style={{ borderTop: '1px solid #e8eaed', paddingTop: 10, display: 'flex', justifyContent: 'space-between', fontWeight: 700, color: '#991b1b', fontSize: 14 }}>
                <span>Total skyldigt beløb</span>
                <span style={{ fontFamily: 'monospace' }}>{formatDKK(paakrav.total_debt_oere)}</span>
              </div>
            </div>

            {/* Frist dates (from backend §93 calculation) */}
            {(paakrav.frist ?? paakrav.cure_deadline) && (
              <div style={{ background: '#fef2f2', borderRadius: 8, padding: '10px 14px', marginBottom: 14, border: '1px solid #fecaca', fontSize: 13 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px' }}>
                  <div>
                    <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 2 }}>Tidligst sendedato (§ 93)</div>
                    <div style={{ fontWeight: 600, color: '#374151' }}>
                      {paakrav.frist
                        ? new Date(paakrav.frist.earliest_send_date).toLocaleDateString('da-DK', { day: 'numeric', month: 'short' })
                        : '—'}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 2 }}>Lejerens betalingsfrist</div>
                    <div style={{ fontWeight: 700, color: '#dc2626' }}>
                      {(paakrav.frist?.deadline ?? paakrav.cure_deadline)
                        ? new Date((paakrav.frist?.deadline ?? paakrav.cure_deadline)!).toLocaleDateString('da-DK', { day: 'numeric', month: 'long', year: 'numeric' })
                        : '—'}
                    </div>
                  </div>
                  {paakrav.frist && (
                    <div style={{ gridColumn: '1 / -1' }}>
                      <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 2 }}>Ophævelse mulig fra</div>
                      <div style={{ fontWeight: 600, color: '#991b1b' }}>
                        {new Date(paakrav.frist.ophavelse_eligible_from).toLocaleDateString('da-DK', { day: 'numeric', month: 'long', year: 'numeric' })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* §182 ophævelsesadvarsel */}
            <div style={{
              background: '#fff5f5', border: '2px solid #e03131', borderRadius: 8,
              padding: '10px 14px', marginBottom: 14, fontSize: 12,
            }}>
              <div style={{ fontWeight: 700, color: '#c92a2a', marginBottom: 4, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Juridisk advarsel — § 182
              </div>
              <div style={{ color: '#c92a2a', lineHeight: 1.5 }}>
                Lejeforholdet vil blive ophævet, såfremt det skyldige beløb ikke betales inden 14 dage fra modtagelse af dette påkrav, jf. lejelovens § 182.
              </div>
            </div>

            {/* Physical letter warning */}
            <div style={{
              background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8,
              padding: '10px 14px', marginBottom: 16, fontSize: 12, color: '#92400e',
            }}>
              <strong>VIGTIGT:</strong> Send også en fysisk kopi til lejerens adresse for at sikre fremkomstbevis jf. lejeloven. E-mail alene er ikke tilstrækkeligt dokumentation.
            </div>
          </>
        ) : null}

        <ComplianceDisclaimer />

        {/* Actions */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
          <button onClick={onClose} style={{
            padding: '7px 14px', borderRadius: 6, fontSize: 13, fontWeight: 500,
            background: '#f9fafb', color: '#374151', border: '1px solid #e5e7eb', cursor: 'pointer',
          }}>Annuller</button>
          <button
            disabled={!canSend || sending}
            onClick={sendPaakrav}
            style={{
              padding: '7px 16px', borderRadius: 6, fontSize: 13, fontWeight: 600,
              background: canSend && !sending ? '#dc2626' : '#f3f4f6',
              color: canSend && !sending ? '#fff' : '#9ca3af',
              border: 'none', cursor: canSend && !sending ? 'pointer' : 'not-allowed',
              fontFamily: 'inherit', transition: 'background 0.12s',
            }}
          >
            {sending ? 'Sender…' : 'Send påkrav (335 kr gebyr)'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Restanceforløb Timeline ──────────────────────────────────────────────────

interface TimelineProps {
  payments: OverduePayment[]
  invoiceMap: Map<string, Invoice | null>
  rykkerMap: Map<string, Rykker[]>
}

const STEPS = ['Opkrævning sendt', 'Rykker 1', 'Rykker 2', 'Rykker 3', 'Påkrav']

function RestanceTimeline({ payments, invoiceMap, rykkerMap }: TimelineProps) {
  if (payments.length === 0) {
    return (
      <div style={{ padding: '32px 24px', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
        Ingen aktive restancer
      </div>
    )
  }

  return (
    <div style={{ padding: '0 20px 20px' }}>
      {payments.map(payment => {
        const invoice = invoiceMap.get(payment.lease_id) ?? null
        const rykkere = invoice ? (rykkerMap.get(invoice.id) ?? []) : []
        const sentCount = rykkere.filter(r => r.status === 'sent').length
        // Step index: 0 = opkrævning sent (always done if invoice exists), 1-3 = rykkere, 4 = påkrav
        const completedSteps = invoice ? 1 + sentCount : 0

        return (
          <div key={payment.id} style={{ marginTop: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
              {payment.lease_unit_no ?? payment.lease_id}
              {payment.renter_name && <span style={{ fontWeight: 400, color: '#9ca3af', marginLeft: 6 }}>{payment.renter_name}</span>}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
              {STEPS.map((step, idx) => {
                const done = idx < completedSteps
                const isPaakrav = idx === 4
                const isActive = idx === completedSteps - 1

                const dotColor = isPaakrav
                  ? '#e5e7eb'
                  : done
                    ? '#16a34a'
                    : '#e5e7eb'
                const labelColor = isPaakrav
                  ? '#9ca3af'
                  : done
                    ? (isActive ? '#dc2626' : '#16a34a')
                    : '#9ca3af'

                return (
                  <React.Fragment key={step}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, minWidth: 0 }}>
                      <div style={{
                        width: 20, height: 20, borderRadius: '50%',
                        background: dotColor,
                        border: `2px solid ${isPaakrav ? '#d1d5db' : done ? '#16a34a' : '#d1d5db'}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        {done && !isPaakrav && (
                          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                            <path d="M2 5l2 2 4-4" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </div>
                      <div style={{
                        fontSize: 10, marginTop: 4, textAlign: 'center', lineHeight: 1.3,
                        color: labelColor, fontWeight: isActive ? 600 : 400,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 64,
                      }}>
                        {step}
                        {isPaakrav && <div style={{ fontSize: 9, color: '#c0c4cc' }}>Kommer snart</div>}
                      </div>
                    </div>
                    {idx < STEPS.length - 1 && (
                      <div style={{
                        height: 2, flex: 1,
                        background: idx < completedSteps - 1 ? '#16a34a' : '#e5e7eb',
                        marginBottom: 18,
                      }} />
                    )}
                  </React.Fragment>
                )
              })}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ─── Overdue Table ─────────────────────────────────────────────────────────────

type FilterKey = 'alle' | 'overdue' | 'partial'

const FILTERS: Array<{ key: FilterKey; label: string }> = [
  { key: 'alle', label: 'Alle' },
  { key: 'overdue', label: 'Overskredet' },
  { key: 'partial', label: 'Delvist betalt' },
]

interface OverdueListProps {
  onPaymentsLoaded: (payments: OverduePayment[]) => void
  invoiceMap: Map<string, Invoice | null>
  rykkerMap: Map<string, Rykker[]>
  onInvoiceFetched: (leaseId: string, invoice: Invoice | null) => void
  onRykkereFetched: (invoiceId: string, rykkere: Rykker[]) => void
  showToast: (text: string, kind: 'success' | 'error') => void
}

function OverdueList({
  onPaymentsLoaded,
  invoiceMap,
  rykkerMap,
  onInvoiceFetched,
  onRykkereFetched,
  showToast,
}: OverdueListProps) {
  const [payments, setPayments] = React.useState<OverduePayment[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [filter, setFilter] = React.useState<FilterKey>('alle')
  const [markPaidTarget, setMarkPaidTarget] = React.useState<OverduePayment | null>(null)
  const [rykkerTarget, setRykkerTarget] = React.useState<OverduePayment | null>(null)
  const [paakravTarget, setPaakravTarget] = React.useState<OverduePayment | null>(null)

  const load = React.useCallback(() => {
    setLoading(true)
    setError(null)
    Promise.all([
      apiFetch<PaymentsResponse | OverduePayment[]>('/api/v1/rent-payments?status=overdue'),
      apiFetch<PaymentsResponse | OverduePayment[]>('/api/v1/rent-payments?status=partial'),
    ]).then(([overdueResp, partialResp]) => {
      function extractItems(resp: PaymentsResponse | OverduePayment[]): OverduePayment[] {
        if (Array.isArray(resp)) return resp
        return resp.items ?? []
      }
      const all = [...extractItems(overdueResp), ...extractItems(partialResp)]
      const seen = new Set<string>()
      const deduped = all.filter(p => { if (seen.has(p.id)) return false; seen.add(p.id); return true })
      setPayments(deduped)
      onPaymentsLoaded(deduped)
    }).catch(() => setError('Kunne ikke hente restancer.'))
      .finally(() => setLoading(false))
  }, [onPaymentsLoaded])

  React.useEffect(() => { (async () => { await load() })() }, [load])

  const filtered = filter === 'alle' ? payments : payments.filter(p => p.status === filter)

  function handleRykkerSent() {
    load()
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Henter…</div>
  if (error) return <div style={{ padding: 20, color: '#dc2626', fontSize: 13 }}>{error}</div>

  return (
    <>
      {/* Filter bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', borderBottom: '1px solid #e8eaed' }}>
        <div style={{ display: 'flex', gap: 5 }}>
          {FILTERS.map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              style={{
                padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: filter === f.key ? 600 : 500,
                background: filter === f.key ? '#4f46e5' : '#f7f8fa',
                color: filter === f.key ? '#ffffff' : '#6b7280',
                border: filter === f.key ? '1px solid #4f46e5' : '1px solid #e8eaed',
                cursor: 'pointer', transition: 'all 0.12s',
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Empty state */}
      {filtered.length === 0 && (
        <div style={{ padding: '48px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>✓</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#16a34a', marginBottom: 4 }}>
            Ingen restancer — alle betalinger er à jour
          </div>
          <div style={{ fontSize: 13, color: '#6b7280' }}>
            {filter !== 'alle' ? 'Prøv at ændre filteret.' : 'Ingen forfaldne eller delvist betalte poster.'}
          </div>
        </div>
      )}

      {/* Table */}
      {filtered.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                {['Lejemål', 'Lejer', 'Måned', 'Forventet', 'Modtaget', 'Mangler', 'Dage overskredet', 'Rykkere', 'Handling'].map(h => (
                  <th key={h} style={{
                    padding: '10px 16px', textAlign: h === 'Handling' ? 'right' : 'left',
                    fontSize: 11, fontWeight: 600, color: '#6b7280',
                    textTransform: 'uppercase', letterSpacing: '0.05em',
                    borderBottom: '1px solid #e8eaed', whiteSpace: 'nowrap',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((payment, i) => {
                const mangler = payment.expected_oere - payment.received_oere
                const days = daysSince1st(payment.period_year, payment.period_month)
                const isLast = i === filtered.length - 1
                const invoice = invoiceMap.get(payment.lease_id) ?? null
                const rykkere = invoice ? (rykkerMap.get(invoice.id) ?? []) : []
                return (
                  <OverdueRow
                    key={payment.id}
                    payment={payment}
                    mangler={mangler}
                    days={days}
                    isLast={isLast}
                    invoice={invoice}
                    rykkere={rykkere}
                    onMarkPaid={() => setMarkPaidTarget(payment)}
                    onSendRykker={() => setRykkerTarget(payment)}
                    onSendPaakrav={() => setPaakravTarget(payment)}
                    onInvoiceFetched={onInvoiceFetched}
                    onRykkereFetched={onRykkereFetched}
                  />
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {markPaidTarget && (
        <MarkPaidModal
          payment={markPaidTarget}
          onClose={() => setMarkPaidTarget(null)}
          onSaved={() => { setMarkPaidTarget(null); load() }}
        />
      )}

      {rykkerTarget && (
        <RykkerModal
          payment={rykkerTarget}
          onClose={() => setRykkerTarget(null)}
          onSent={handleRykkerSent}
          showToast={showToast}
        />
      )}

      {paakravTarget && (
        <PaakravModal
          payment={paakravTarget}
          onClose={() => setPaakravTarget(null)}
          onSent={() => { setPaakravTarget(null); load() }}
          showToast={showToast}
        />
      )}
    </>
  )
}

function OverdueRow({
  payment, mangler, days, isLast, invoice, rykkere: _rykkere, // eslint-disable-line @typescript-eslint/no-unused-vars
  onMarkPaid, onSendRykker, onSendPaakrav, onInvoiceFetched, onRykkereFetched,
}: {
  payment: OverduePayment
  mangler: number
  days: number
  isLast: boolean
  invoice: Invoice | null
  rykkere: Rykker[]
  onMarkPaid: () => void
  onSendRykker: () => void
  onSendPaakrav: () => void
  onInvoiceFetched: (leaseId: string, invoice: Invoice | null) => void
  onRykkereFetched: (invoiceId: string, rykkere: Rykker[]) => void
}) {
  const [hovered, setHovered] = React.useState(false)
  const isPartial = payment.status === 'partial'
  const invoiceFetchedRef = React.useRef(false)

  // Lazily fetch invoice + rykkere on hover
  async function fetchLazyData() {
    if (invoiceFetchedRef.current) return
    invoiceFetchedRef.current = true
    try {
      const inv = await fetchOpenInvoiceForPayment(payment)
      onInvoiceFetched(payment.lease_id, inv)
      if (inv) {
        const rList = await apiFetch<Rykker[]>(`/api/v1/rykkere?invoice_id=${inv.id}`)
        onRykkereFetched(inv.id, Array.isArray(rList) ? rList : [])
      }
    } catch {
      onInvoiceFetched(payment.lease_id, null)
    }
  }

  return (
    <tr
      onMouseEnter={() => { setHovered(true); fetchLazyData() }}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? '#f9fafb' : 'transparent',
        transition: 'background 0.1s',
        borderBottom: isLast ? 'none' : '1px solid #f3f4f6',
      }}
    >
      <td style={{ padding: '11px 16px' }}>
        <div style={{ fontWeight: 500, color: '#111827' }}>{payment.lease_unit_no ?? payment.lease_id.slice(0, 8)}</div>
        {payment.lease_address && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{payment.lease_address}</div>}
      </td>
      <td style={{ padding: '11px 16px', color: '#6b7280' }}>{payment.renter_name ?? '—'}</td>
      <td style={{ padding: '11px 16px', color: '#6b7280', whiteSpace: 'nowrap' }}>
        {MONTHS_DA[payment.period_month - 1]} {payment.period_year}
      </td>
      <td style={{ padding: '11px 16px', color: '#111827', fontFamily: 'monospace', fontSize: 12 }}>
        {formatDKK(payment.expected_oere)}
      </td>
      <td style={{ padding: '11px 16px', color: isPartial ? '#d97706' : '#9ca3af', fontFamily: 'monospace', fontSize: 12 }}>
        {payment.received_oere > 0 ? formatDKK(payment.received_oere) : <span style={{ color: '#d1d5db' }}>—</span>}
      </td>
      <td style={{ padding: '11px 16px', fontFamily: 'monospace', fontSize: 12 }}>
        <span style={{ color: '#dc2626', fontWeight: 600 }}>{formatDKK(mangler)}</span>
      </td>
      <td style={{ padding: '11px 16px' }}>
        <span style={{
          padding: '2px 8px', borderRadius: 10, fontSize: 12, fontWeight: 500,
          background: days > 30 ? '#fef2f2' : '#fffbeb',
          color: days > 30 ? '#dc2626' : '#d97706',
          border: `1px solid ${days > 30 ? '#fecaca' : '#fde68a'}`,
        }}>
          {days} dage
        </span>
      </td>
      <td style={{ padding: '11px 16px' }}>
        <RykkerBadge invoiceId={invoice?.id ?? null} />
      </td>
      <td style={{ padding: '11px 16px', textAlign: 'right' }}>
        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}>
          <button
            onClick={onSendRykker}
            style={secondaryBtnStyle}
          >
            Send rykker
          </button>
          <button
            onClick={onSendPaakrav}
            title="Send formelt påkrav (lejelov § 182)"
            style={{
              ...secondaryBtnStyle,
              background: '#fff5f5',
              color: '#c92a2a',
              border: '1px solid #fecaca',
              fontWeight: 600,
            }}
          >
            Send påkrav
          </button>
          <Button size="sm" variant="secondary" onClick={onMarkPaid}>
            Markér betalt
          </Button>
        </div>
      </td>
    </tr>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function RestancePage() {
  const [payments, setPayments] = React.useState<OverduePayment[]>([])
  const [loadingCount, setLoadingCount] = React.useState(true)
  // Shared invoice + rykker cache for timeline
  const [invoiceMap, setInvoiceMap] = React.useState<Map<string, Invoice | null>>(new Map())
  const [rykkerMap, setRykkerMap] = React.useState<Map<string, Rykker[]>>(new Map())
  const { toasts, show: showToast, remove: removeToast } = useToast()

  React.useEffect(() => {
    Promise.all([
      apiFetch<PaymentsResponse | OverduePayment[]>('/api/v1/rent-payments?status=overdue').catch(() => []),
      apiFetch<PaymentsResponse | OverduePayment[]>('/api/v1/rent-payments?status=partial').catch(() => []),
    ]).then(([o, p]) => {
      function extractItems(resp: PaymentsResponse | OverduePayment[]): OverduePayment[] {
        if (Array.isArray(resp)) return resp
        return resp.items ?? []
      }
      const all = [...extractItems(o), ...extractItems(p)]
      const seen = new Set<string>()
      setPayments(all.filter(x => { if (seen.has(x.id)) return false; seen.add(x.id); return true }))
    }).finally(() => setLoadingCount(false))
  }, [])

  const count = loadingCount ? null : payments.length

  function handleInvoiceFetched(leaseId: string, invoice: Invoice | null) {
    setInvoiceMap(prev => new Map(prev).set(leaseId, invoice))
  }

  function handleRykkereFetched(invoiceId: string, rykkere: Rykker[]) {
    setRykkerMap(prev => new Map(prev).set(invoiceId, rykkere))
  }

  return (
    <AppLayout>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#111827', marginBottom: 2 }}>Restancer</div>
          <div style={{ fontSize: 13, color: '#6b7280' }}>Overskredet og delvist betalte opkrævninger</div>
        </div>
        {count !== null && count > 0 && (
          <span style={{
            background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca',
            borderRadius: 20, padding: '2px 10px', fontSize: 12, fontWeight: 600,
          }}>
            {count}
          </span>
        )}
      </div>

      <Card padding="none">
        <OverdueList
          onPaymentsLoaded={setPayments}
          invoiceMap={invoiceMap}
          rykkerMap={rykkerMap}
          onInvoiceFetched={handleInvoiceFetched}
          onRykkereFetched={handleRykkereFetched}
          showToast={showToast}
        />
      </Card>

      {/* Restanceforløb */}
      <div style={{ marginTop: 16 }}>
        <Card padding="none">
          <div style={{ padding: '12px 20px', borderBottom: '1px solid #e8eaed', fontSize: 13, fontWeight: 600, color: '#111827' }}>
            Restanceforløb
          </div>
          <RestanceTimeline
            payments={payments}
            invoiceMap={invoiceMap}
            rykkerMap={rykkerMap}
          />
        </Card>
      </div>

      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </AppLayout>
  )
}
